import dayjs from 'dayjs';
import { DuAnDetailView } from '@/types/action-plan/DuAnDetailView';
import { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { DuAnCongViecTree, DuAnCongViecTienDoTree, RoleItemTree } from '@/types/action-plan/DuAnView';
import { IRoleModule } from '@/types/roleGroup';
import { ColumnKanBanDTO } from '@/types/action-plan/ActionPlan/ColumnKanBanDTO';
import CommonJs from '@/utils/commonJs';

// ==================== INTERFACES ====================

export interface Task {
  id: string;
  taskName: string;
  thoiGian: string;
  hoanThanh: number;
  trangThai: number;
  countChilds: number;
  nganSachThucHien: number;
  nguoiPhuTrachID: string;
  nguoiHoTroID: string;
  nguoiTao: string;
  icon: string | null;
  mtctid: string;
  completionCount: number;
  completionPercentage: number;
  dueDate: string;
  assignedTo: string;
  support: string;
  creator: string;
  budgetPercentage: number;
  status: 'pending' | 'active' | 'success';
  isGoalRow: boolean;
  level: number;
  originalData?: DuAnCongViecTree;
  /** true khi mtctid không nằm trong listMucTieuPhuTrachIDs (mục tiêu không phụ trách) */
  isMucTieuKhongPhuTrach?: boolean;
  /** Dự án thuộc dòng mục tiêu — dùng cho Table expandable */
  children?: Task[];
}

/**
 * Interface cho ColumnItem trong Kanban
 */
export interface ColumnItemKanban {
  id: number | string;
  columnName: string;
  progressStatus: 'Có' | 'Không';
  isVisible: boolean;
  originalData?: ColumnKanBanDTO;
}

/**
 * Interface cho ColumnItem trong Childs
 */
export interface ColumnItemChild {
  id: number | string;
  projectName: string;
  person: string;
  dateTime: string;
  viTri?: number;
  completionCount?: number;
  completionPercentage?: number;
  originalData?: DuAnCongViecTree;
}

// ==================== DEFAULT MODELS ====================

/**
 * Default Role Module (IRoleModule)
 */
export const DEFAULT_ROLE_MODULE: IRoleModule = {
  id: 0,
  parentID: 0,
  name: '',
  site: '',
  group: '',
  controller: '',
  action: '',
  read: false,
  create: false,
  update: false,
  delete: false,
  import: false,
  export: false,
  allBP: false,
  allBPCon: false,
  print: false,
  allCT: false,
  filter: false,
  fullcontrol: false,
  quanLy: false,
  setupFormQT: false,
};

/**
 * Default RoleItemTree
 */
export const DEFAULT_ROLE_ITEM_TREE: RoleItemTree = {
  isNguoiTao: false,
  isPhuTrach: false,
  isHoTro: false,
  hasChild: false,
  isEdit: false,
  hasAddSub: false,
  isRoot: false,
  isView: false,
  isDelete: false,
};

/**
 * Default DuAnCongViecTienDoTree (trongKy, luyke)
 */
export const DEFAULT_TIEN_DO_TREE: DuAnCongViecTienDoTree = {
  icon: '',
  titleIcon: '',
  giaiDoan: '',
  tienDoHomNay: 0,
  hoanThanh: 0,
  trangThai: 1,
};

/**
 * Default DuAnCongViecDTO (công việc)
 */
export const createDefaultDuAnCongViecDTO = (
  overrides?: Partial<DuAnCongViecDTO>
): DuAnCongViecDTO => {
  return {
    id: CommonJs.createTempID(),
    tenCongViec: 'Tên dự án',
    nguoiPhuTrachID: '',
    nguoiHoTroID: '',
    ngayBatDau: undefined,
    ngayKetThuc: undefined,
    nganSach: 0,
    tiTrong: 0,
    mtctid: '',
    duAnID: '',
    moTa: '',
    filesDinhKem: '',
    site: '',
    viTri: 0,
    kanBanViTri: 0,
    chiTieu: 0,
    tanSuat: 1,
    hinhThucTongHop: 1,
    showAlert: false,
    completeQty: 0,
    hasChild: false,
    viecDuocGiao: false,
    stateApproved: 0,
    isDuAn: true,
    phuTrachMucTieu: '',
    year: undefined,
    parentID: '',
    hienThiTrangQuanLy: true,
    ...overrides,
  } as DuAnCongViecDTO;
};

/**
 * Default DuAnCongViecDTO cho child (công việc con)
 */
export const createDefaultChildCongViecDTO = (
  parentCongViec?: Partial<DuAnCongViecDTO>,
  overrides?: Partial<DuAnCongViecDTO>
): DuAnCongViecDTO => {
  return createDefaultDuAnCongViecDTO({
    ...parentCongViec,
    isDuAn: false,
    ...overrides,
  });
};

/**
 * Default DuAnCongViecTree
 */
export const createDefaultDuAnCongViecTree = (
  congViec?: Partial<DuAnCongViecDTO>,
  overrides?: Partial<DuAnCongViecTree>
): DuAnCongViecTree => {
  return {
    congViec: createDefaultDuAnCongViecDTO(congViec),
    thoiGian: '',
    hasInRange: false,
    hasChange: true,
    hasChild: false,
    param: '',
    trongKy: DEFAULT_TIEN_DO_TREE,
    luyke: DEFAULT_TIEN_DO_TREE,
    childs: [],
    countChilds: 0,
    tenTanSuat: '',
    tenHinhThucTinh: '',
    roleItemTree: DEFAULT_ROLE_ITEM_TREE,
    tiTrongDaAn: 0,
    tiTrongTatCaDaAn: 0,
    idTemp: '',
    ...overrides,
  };
};

/**
 * Default ColumnItem cho Kanban (initialColumns)
 */
export const DEFAULT_KANBAN_COLUMNS: ColumnItemKanban[] = [];

/**
 * Default ColumnItem cho Childs (initialColumns)
 */
export const DEFAULT_CHILD_COLUMNS: ColumnItemChild[] = [];

/**
 * Tạo ColumnItem mới cho Kanban
 */
export const createNewKanbanColumn = (
  columnName: string = 'Tên cột mới',
  duAnID?: string,
  site?: string
): ColumnKanBanDTO => {
  return {
    id: CommonJs.createTempID(),
    tenCot: columnName,
    tienDo: 0, // 0 = Không, 1 = Có
    trangThai: true,
    viTri: 0,
    duAnID: duAnID || '',
    site: site || '',
    isDelete: false,
  } as ColumnKanBanDTO;
};

/**
 * Tạo ColumnItem mới cho Child
 */
export const createNewChildColumn = (
  projectName: string = 'Tên công việc',
  person: string = '',
  dateTime: string = dayjs().format('DD/MM/YYYY'),
  viTri: number = 0
): ColumnItemChild => {
  return {
    id: CommonJs.createTempID(),
    projectName,
    person,
    dateTime,
    viTri,
  };
};

/**
 * Default DuAn object
 */
export const DEFAULT_DUAN = {
  id: 'undefined',
  name: '',
  originalData: null,
};

/**
 * Default form values khi tạo mới
 */
export const createDefaultFormValues = (profileId?: string) => {
  const today = dayjs();
  return {
    projectName: 'Tên dự án',
    nv: profileId || undefined,
    nvSupport: undefined,
    nvNguoiTao: profileId || undefined,
    datetime: [today, today],
    unit: 0,
    tiTrong: 0,
    viTri: 0,
    target: undefined,
    duAn: undefined,
    description: '',
    filesDinhKem: undefined,
    hienThiTrangQuanLy: true,
    childsColumns: DEFAULT_CHILD_COLUMNS,
    listKanbans: [],
  };
};

/**
 * Default DuAnDetailView payload khi tạo mới
 */
export const createDefaultDuAnDetailView = (
  values: {
    target: string;
    targetName?: string;
    nguoiPhuTrachID?: string;
    nguoiHoTroID?: string;
    ngayBatDau?: Date;
    ngayKetThuc?: Date;
    nganSach?: number;
    tiTrong?: number;
    viTri?: number;
    duAnID?: string;
    moTa?: string;
    filesDinhKem?: string;
    projectName?: string;
    selectbophan?: string;
    tenantID?: string;
    year?: number;
    thoiGian?: string;
    minDateChild?: string;
    maxDateChild?: string;
    listKanbans?: ColumnKanBanDTO[];
    childsColumns?: ColumnItemChild[];
  }
): DuAnDetailView => {
  const today = dayjs();
  const ngayBatDau = values.ngayBatDau || today.toDate();
  const ngayKetThuc = values.ngayKetThuc || today.toDate();
  const thoiGian =
    values.thoiGian ||
    `${dayjs(ngayBatDau).format('DD/MM/YYYY')} - ${dayjs(ngayKetThuc).format('DD/MM/YYYY')}`;

  return {
    sumNganSachChild: 0,
    roleItemTree: {} as RoleItemTree,
    phanHe: 'bsc',
    thoiGian: thoiGian,
    minDateChild: values.minDateChild || dayjs(ngayBatDau).format('YYYY-MM-DD'),
    maxDateChild: values.maxDateChild || dayjs(ngayKetThuc).format('YYYY-MM-DD'),
    selectbophan: values.selectbophan || '',
    mucTieuID: values.target || '',
    mucTieuTen: values.targetName || '',
    nhanViens: [],
    boPhans: [],
    chucVus: [],
    role: DEFAULT_ROLE_MODULE,
    message: '',
    avatarNguoiTao: '',
    avatarPhuTrach: '',
    strAliasNhanSuDuAn: '',
    ddlViTri: [],
    listKanbans: values.listKanbans || [],
    listAvatarHoTro: [],
    duAnCongViec: createDefaultDuAnCongViecTree({
      id: CommonJs.createTempID(),
      tenCongViec: values.projectName || 'Tên dự án',
      nguoiPhuTrachID: values.nguoiPhuTrachID || '',
      nguoiHoTroID: values.nguoiHoTroID || '',
      ngayBatDau: ngayBatDau,
      ngayKetThuc: ngayKetThuc,
      nganSach: values.nganSach || 0,
      tiTrong: values.tiTrong || 0,
      mtctid: values.target || '',
      duAnID: values.duAnID || '',
      moTa: values.moTa || '',
      filesDinhKem: values.filesDinhKem || '',
      site: values.tenantID || '',
      viTri: values.viTri || 0,
      kanBanViTri: 0,
      chiTieu: 0,
      tanSuat: 1,
      hinhThucTongHop: 1,
      showAlert: false,
      completeQty: 0,
      hasChild: false,
      viecDuocGiao: false,
      stateApproved: 0,
      isDuAn: true,
      phuTrachMucTieu: values.selectbophan || '',
      year: values.year,
    }),
  };
};

/**
 * Tạo parentCongViec từ form values (dùng khi tạo child)
 */
export const createParentCongViecFromForm = (
  formValues: {
    projectName?: string;
    nv?: string | string[];
    nvSupport?: string | string[];
    datetime?: [dayjs.Dayjs, dayjs.Dayjs];
    unit?: number;
    tiTrong?: number;
    viTri?: number;
    target?: string;
    duAn?: string;
    description?: string;
    filesDinhKem?: string;
  },
  overrides?: Partial<DuAnCongViecDTO>
): DuAnCongViecDTO => {
  const nguoiHoTroID =
    formValues.nvSupport && Array.isArray(formValues.nvSupport)
      ? formValues.nvSupport.filter((id: string) => id && id.trim()).join(',')
      : formValues.nvSupport || '';

  const nguoiPhuTrachJoined =
    formValues.nv == null || formValues.nv === ''
      ? ''
      : Array.isArray(formValues.nv)
        ? formValues.nv.filter((id: string) => id && String(id).trim()).join(',')
        : String(formValues.nv).trim();

  return createDefaultDuAnCongViecDTO({
    id: '',
    tenCongViec: formValues.projectName || 'Tên dự án',
    nguoiPhuTrachID: nguoiPhuTrachJoined,
    nguoiHoTroID: nguoiHoTroID,
    nganSach: formValues.unit || 0,
    tiTrong: formValues.tiTrong || 0,
    viTri: formValues.viTri || 0,
    mtctid: formValues.target || '',
    duAnID: formValues.duAn || '',
    moTa: formValues.description || '',
    filesDinhKem: formValues.filesDinhKem || '',
    isDuAn: false,
    ...overrides,
  });
};

